/**
 * Plakadan kararlı araç rengi.
 *
 * Algoritma (basit, port edilebilir): plaka normalleştirilir (TÜM boşluklar silinir, yalnız ASCII
 * a-z büyük harfe çevrilir; dile bağlı büyük harf kuralı YOK), ardından FNV-1a 32-bit hash
 * (UTF-16 kod birimi bazlı), ardından murmur3 fmix32 karıştırması (ardışık plakalar düşük bitlerde
 * kümelenmesin diye) ve mod 10 ile paletten renk seçilir. `vehicleColor`: aynı plaka her zaman aynı
 * renk, başka araçlardan bağımsız. `vehicleColors` (filo): çakışmasızlık için ekleme çakışan
 * araçların rengini kaydırabilir. Flutter portu aynı adımları izler.
 */

const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/** Plakayı hash için normalleştir: tüm boşlukları sil, ASCII a-z → A-Z (dil bağımsız). */
export function normalizePlateForColor(plate: string): string {
  return plate.replace(/\s+/g, '').replace(/[a-z]/g, (c) => c.toUpperCase());
}

/** murmur3 fmix32: 32-bit son karıştırma (Math.imul + kaydırma; Dart portu aynı adımlarla). */
export function fmix32(h: number): number {
  let x = h >>> 0;
  x ^= x >>> 16;
  x = Math.imul(x, 0x85ebca6b);
  x ^= x >>> 13;
  x = Math.imul(x, 0xc2b2ae35);
  x ^= x >>> 16;
  return x >>> 0;
}

/** FNV-1a 32-bit (UTF-16 kod birimi bazlı). Deterministik, platform bağımsız. */
export function fnv1a32(input: string): number {
  let hash = FNV_OFFSET_BASIS;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, FNV_PRIME);
  }
  return hash >>> 0;
}

/**
 * 10 renkli palet: mavi, yeşil, kırmızı, turuncu, mor, camgöbeği, eflatun, zeytin, arduvaz, kahverengi
 * — hepsi ayrı ton ailesinde. Hepsi beyaz yazıyla WCAG AA (>= 4.5:1) kontrast verecek kadar koyu.
 * Kontrastlar: #1E5BB8 6.5 · #26773A 5.6 · #C92A2A 5.5 · #B85C00 4.6 · #7048E8 5.6 · #0B7285 5.6 ·
 * #A9209A 6.3 · #5C7C0A 4.8 · #495057 8.2 · #7A4A21 7.4. Açık gri #9AA0A6 yalnız "plaka yok" içindir.
 */
export const VEHICLE_COLOR_PALETTE = [
  '#1E5BB8', // mavi
  '#26773A', // yeşil
  '#C92A2A', // kırmızı
  '#B85C00', // turuncu
  '#7048E8', // mor
  '#0B7285', // camgöbeği
  '#A9209A', // eflatun
  '#5C7C0A', // zeytin
  '#495057', // arduvaz
  '#7A4A21', // kahverengi
] as const;

/** Plakadan paletten renk seç. Boş plaka → gri (güvenli varsayılan). */
export function vehicleColor(plate: string): string {
  const normalized = normalizePlateForColor(plate);
  if (!normalized) return '#9AA0A6';
  return VEHICLE_COLOR_PALETTE[hashBucket(normalized)]!;
}

function hashBucket(normalizedPlate: string): number {
  return fmix32(fnv1a32(normalizedPlate)) % VEHICLE_COLOR_PALETTE.length;
}

/**
 * Bir araç listesi için çakışmasız renk eşlemesi (anahtar: verilen plaka metni).
 * Plakalar normalize edilip SIRALANIR; her plaka önce kendi hash kovasını dener, doluysa sıradaki
 * boş palet rengini alır (doğrusal yoklama, mod palet). En çok palet boyutu (10) araçta iki araç
 * ASLA aynı renkte olmaz; palet dolunca hash rengine döner. Araç eklenince yalnız çakışan
 * araçların rengi değişebilir. Girdi sırası sonucu etkilemez. Boş plaka gri alır.
 */
export function vehicleColors(plates: readonly string[]): Map<string, string> {
  const normalizedSet = [
    ...new Set(plates.map(normalizePlateForColor).filter((p) => p !== '')),
  ].sort();
  const taken = new Set<number>();
  const byNormalized = new Map<string, string>();
  for (const p of normalizedSet) {
    const n = VEHICLE_COLOR_PALETTE.length;
    const start = hashBucket(p);
    let idx = start;
    if (taken.size < n) {
      while (taken.has(idx)) idx = (idx + 1) % n;
      taken.add(idx);
    }
    byNormalized.set(p, VEHICLE_COLOR_PALETTE[idx]!);
  }
  return new Map(plates.map((p) => [p, byNormalized.get(normalizePlateForColor(p)) ?? '#9AA0A6']));
}
